import { ContactForm } from '@/components/apropos/ContactForm';
import { Principes } from '@/components/Principes';
import { Accent } from '@/components/ui/Accent';
import { ButtonLink } from '@/components/ui/ButtonLink';
import { Container } from '@/components/ui/Container';
import { Icon } from '@/components/ui/Icon';
import { Label } from '@/components/ui/Label';
import { Section } from '@/components/ui/Section';
import { SectionHead } from '@/components/ui/SectionHead';
import { contact, gardeFous, intro, labo } from '@/content/about';
import { site, socialHref, socials } from '@/content/site';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { cn } from '@/lib/cn';

export function AboutPage() {
  useDocumentMeta({
    title: `À propos, ${site.name}`,
    description: intro.lead,
    path: '/a-propos',
  });

  return (
    <>
      <section aria-labelledby="apropos-titre" className="pt-12 pb-10 tab:pt-21 tab:pb-18">
        <Container className="grid items-end gap-10 tab:grid-cols-[minmax(0,1fr)_280px] desk:grid-cols-[minmax(0,1fr)_380px] desk:gap-16">
          <div>
            <Label>À propos</Label>
            <h1 id="apropos-titre" className="mt-5.5 text-title leading-none">
              {intro.title}
              <br />
              <Accent text={intro.title2} />
            </h1>
            <p className="mt-4.5 max-w-[40em] text-sub text-encre-soft">{intro.lead}</p>
            <dl className="m-0 mt-9 flex flex-wrap gap-9 border-t border-line pt-5">
              {intro.facts.map((fact) => (
                <div key={fact.label} className="flex flex-col gap-1">
                  <dt className="label text-encre-mute">{fact.label}</dt>
                  <dd className="m-0 text-body-xs font-medium tabular-nums">{fact.value}</dd>
                </div>
              ))}
            </dl>
          </div>
          {/* Emplacement provisoire : à remplacer par le portrait 4:5 (maquette). */}
          <img
            src="/portrait.svg"
            alt="Emplacement du portrait"
            width={380}
            height={475}
            className="aspect-[4/5] w-full max-w-[280px] rounded-xl object-cover tab:max-w-none"
          />
        </Container>
      </section>

      <Section tone="blanc" aria-labelledby="regle-titre">
        <Container>
          <SectionHead id="regle-titre" eyebrow="La règle" title={`${site.tagline}.`} />
          <Principes />
        </Container>
      </Section>

      <Section aria-labelledby="labo-titre">
        <Container>
          <SectionHead
            id="labo-titre"
            eyebrow={`Le labo, mis à jour le ${labo.updated}`}
            title="Ce que je fais tourner."
          />
          <ul className="m-0 grid list-none gap-5 p-0 tab:grid-cols-2 desk:grid-cols-3">
            {labo.components.map((component) => {
              const active = component.state === 'actif';
              return (
                <li
                  key={component.title}
                  className={cn(
                    'flex flex-col gap-2 rounded-lg px-6 py-5.5',
                    active ? 'bg-blanc' : 'border border-dashed border-encre/25',
                  )}
                >
                  <Label className={cn(active && 'inline-flex items-center gap-[7px] before:size-[7px] before:rounded-full before:bg-ciel before:content-[""]')}>
                    {component.label}
                  </Label>
                  <h3 className={cn('text-h6 font-semibold', !active && 'text-encre-mute')}>
                    {component.title}
                  </h3>
                  <p className={cn('text-body-xs', active ? 'text-encre-soft' : 'text-encre-mute')}>
                    {component.body}
                  </p>
                </li>
              );
            })}
          </ul>
        </Container>
      </Section>

      <Section tone="encre" aria-labelledby="garde-fous-titre">
        <Container>
          <Label tone="clair">Garde-fous</Label>
          <h2 id="garde-fous-titre" className="mt-4 text-h2">
            Ce que je m&apos;impose.
          </h2>
          <ol className="m-0 mt-10 grid list-none border-t border-blanc/20 p-0 tab:grid-cols-2 tab:gap-x-12">
            {gardeFous.map((rule, index) => (
              <li
                key={rule}
                className="grid grid-cols-[44px_minmax(0,1fr)] gap-3 border-b border-blanc/12 py-5"
              >
                <Label tone="ciel">{String(index + 1).padStart(2, '0')}</Label>
                <p className="text-blanc/85">{rule}</p>
              </li>
            ))}
          </ol>
        </Container>
      </Section>

      <Section aria-labelledby="reseaux-titre">
        <Container>
          <SectionHead id="reseaux-titre" eyebrow="Réseaux" title="Me suivre." />
          <ul className="m-0 grid list-none gap-5 p-0 tab:grid-cols-2 desk:grid-cols-4">
            {socials.map((social) => (
              <li key={social.id}>
                <a
                  href={socialHref(social)}
                  className="flex h-full flex-col gap-3 rounded-lg bg-blanc p-6 text-encre no-underline transition-transform hover:-translate-y-[3px]"
                >
                  <span className="flex size-12 items-center justify-center rounded-md bg-encre text-blanc">
                    <Icon name={social.icon} size={22} />
                  </span>
                  <b className="text-h5">{social.label}</b>
                  <p className="text-body-xs text-encre-soft">{social.blurb}</p>
                  <Label className="mt-auto">{social.handle}</Label>
                </a>
              </li>
            ))}
          </ul>
        </Container>
      </Section>

      <Section id="contact" tone="encre" aria-labelledby="contact-titre">
        <Container className="grid items-start gap-9 desk:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] desk:gap-16">
          <div>
            <Label tone="clair">Contact</Label>
            <h2 id="contact-titre" className="mt-4 text-h2">
              <Accent text="Me |contacter." variant="ciel" />
            </h2>
            <p className="mt-4.5 max-w-[40em] text-sub text-blanc/78">{contact.lead}</p>
            <ButtonLink to={`mailto:${site.email}`} variant="ciel" icon="mail" className="mt-7">
              {site.email}
            </ButtonLink>
          </div>
          <ContactForm />
        </Container>
      </Section>
    </>
  );
}
